import { Address, RecurringMeeting } from '../../generated'
import { describeRrule } from '../../utils/Rrule.ts'

interface ScheduleListProps {
  meetings: RecurringMeeting[]
  addresses: Address[]
  onEdit: (meeting: RecurringMeeting) => void
  onRemove: (payload: { meetingId: string; name: string }) => void
}

interface ScheduleListItemProps {
  meeting: RecurringMeeting
  addressName: string
  onEdit: ScheduleListProps['onEdit']
  onRemove: ScheduleListProps['onRemove']
}

// rrule's toText can throw on malformed patterns; never let that break the list.
const safeDescribe = (rrule: string): string => {
  try {
    return describeRrule(rrule)
  } catch {
    return 'Custom recurrence'
  }
}

// "7:00 PM" from an epoch-seconds timestamp in the user's local zone.
const formatStartTime = (epochSeconds: number): string =>
  new Date(epochSeconds * 1000).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })

const ScheduleListItem = ({
  meeting,
  addressName,
  onEdit,
  onRemove,
}: ScheduleListItemProps) => (
  <li className="flex items-start justify-between gap-4 py-4">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <p className="font-medium text-gray-900">{meeting.name}</p>
        {meeting.isPrimary && (
          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
            Primary
          </span>
        )}
      </div>
      <p className="mt-1 text-sm capitalize text-gray-600">
        {safeDescribe(meeting.rrule)}
      </p>
      <p className="mt-0.5 text-sm text-gray-500">
        {formatStartTime(meeting.startTimestamp)} ({meeting.durationMin} min)
        {' · '}
        {addressName}
      </p>
    </div>
    <div className="flex flex-shrink-0 items-center gap-4 text-sm">
      <button
        type="button"
        onClick={() => onEdit(meeting)}
        className="text-indigo-600 hover:text-indigo-900">
        Edit
      </button>
      <button
        type="button"
        onClick={() =>
          onRemove({ meetingId: String(meeting.id), name: meeting.name })
        }
        className="text-red-600 hover:text-red-800">
        Remove
      </button>
    </div>
  </li>
)

const ScheduleList = ({
  meetings,
  addresses,
  onEdit,
  onRemove,
}: ScheduleListProps) => (
  <ul className="divide-y divide-gray-200 border-t">
    {meetings.map((meeting) => (
      <ScheduleListItem
        key={meeting.id}
        meeting={meeting}
        addressName={
          addresses.find((address) => address.id === meeting.addressId)?.name ??
          'Unknown address'
        }
        onEdit={onEdit}
        onRemove={onRemove}
      />
    ))}
  </ul>
)

export default ScheduleList
